<template>
  <section
    id="hero"
    class="home-hero"
    aria-label="首页焦点内容"
    @mouseenter="pauseOnHover"
    @mouseleave="resumeAfterHover"
  >
    <div
      v-for="(banner, index) in slides"
      :key="banner.id"
      class="hero-slide"
      :class="{ active: index === currentIndex }"
      :aria-hidden="index === currentIndex ? undefined : true"
    >
      <home-media
        :source="banner.image_url"
        :mobile-source="banner.mobile_image_url"
        fallback-source="/images/company/strength/pilot-base.jpg"
        :alt="banner.image_alt || banner.title || '兴科官网焦点图片'"
        loading="eager"
        class="hero-media"
      />
    </div>

    <div class="page-container hero-content">
      <transition name="hero-copy" mode="out-in">
        <div :key="currentSlide.id" class="hero-copy">
          <!-- <span v-if="currentSlide.eyebrow" class="hero-eyebrow">{{ currentSlide.eyebrow }}</span> -->
          <h1>{{ currentSlide.title }}</h1>
          <p v-if="currentSlide.subtitle">{{ currentSlide.subtitle }}</p>
          <div class="hero-actions">
            <nuxt-link class="hero-button hero-button-primary" to="/company/profile">
              了解兴科
              <i class="i-custom-enter wh-16px" aria-hidden="true"></i>
            </nuxt-link>
            <contact-dialog default-type="合作咨询">
              <button class="hero-button hero-button-secondary" type="button">
                合作咨询
                <i class="i-custom-enter wh-16px" aria-hidden="true"></i>
              </button>
            </contact-dialog>
          </div>
        </div>
      </transition>
    </div>

    <div v-if="slides.length > 1" class="hero-arrows" role="group" aria-label="切换 Banner">
      <button
        class="hero-arrow hero-arrow-prev"
        type="button"
        aria-label="上一张 Banner"
        title="上一张"
        @click="handleSlide(currentIndex - 1, $event)"
      >
        <i class="i-lucide-chevron-left" aria-hidden="true"></i>
      </button>
      <button
        class="hero-arrow hero-arrow-next"
        type="button"
        aria-label="下一张 Banner"
        title="下一张"
        @click="handleSlide(currentIndex + 1, $event)"
      >
        <i class="i-lucide-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <div v-if="slides.length > 1" class="hero-controls">
      <div class="page-container hero-control-inner">
        <div class="hero-dots" aria-label="切换 Banner">
          <button
            v-for="(banner, index) in slides"
            :key="banner.id"
            class="hero-dot"
            :class="{ active: index === currentIndex }"
            type="button"
            :aria-label="`查看第 ${index + 1} 张 Banner`"
            :aria-pressed="index === currentIndex"
            @click="setSlide(index)"
          ></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { BannerItem } from '~/types/site-api'

  defineOptions({
    name: 'HomeHero',
  })

  const props = withDefaults(
    defineProps<{
      banners?: BannerItem[]
    }>(),
    {
      banners: () => [],
    }
  )

  const fallbackBanner: BannerItem = {
    button_text: '了解更多',
    eyebrow: '热带特色作物科技与产业服务',
    id: 'fallback-banner',
    image_alt: '海南兴科综合实验楼',
    image_url: '/images/company/strength/pilot-base.jpg',
    mobile_image_url: '/images/company/strength/pilot-base.jpg',
    position: null,
    subtitle: '贯通科技创新、中试验证、成果转化与产品产业化，为热带特色农业发展提供持续动力。',
    title: '让科研成果走出实验室，走向产业与生活',
    url: null,
  }

  const slides = computed(() => (props.banners.length > 0 ? props.banners : [fallbackBanner]))
  const currentIndex = shallowRef(0)
  const hoverPaused = shallowRef(false)
  const reducedMotion = shallowRef(false)
  let timer: ReturnType<typeof setInterval> | undefined

  const currentSlide = computed(() => slides.value[currentIndex.value] ?? fallbackBanner)

  function clearTimer() {
    if (timer) clearInterval(timer)
    timer = undefined
  }

  function startTimer() {
    clearTimer()
    if (hoverPaused.value || reducedMotion.value || slides.value.length < 2) return
    timer = setInterval(() => {
      currentIndex.value = (currentIndex.value + 1) % slides.value.length
    }, 6200)
  }

  function setSlide(index: number) {
    currentIndex.value = (index + slides.value.length) % slides.value.length
    startTimer()
  }

  function handleSlide(index: number, event: MouseEvent) {
    setSlide(index)

    const target = event.currentTarget
    if (event.detail > 0 && target instanceof HTMLElement) target.blur()
  }

  function pauseOnHover() {
    if (!window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches)
      return
    hoverPaused.value = true
    clearTimer()
  }

  function resumeAfterHover() {
    if (!hoverPaused.value) return
    hoverPaused.value = false
    startTimer()
  }

  watch(
    () => props.banners,
    () => {
      if (currentIndex.value >= slides.value.length) currentIndex.value = 0
      startTimer()
    }
  )

  onMounted(() => {
    reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    startTimer()
  })

  onUnmounted(clearTimer)
</script>

<style scoped lang="scss">
  .home-hero {
    position: relative;
    min-height: min(640px, calc(100dvh - 126px));
    max-height: 680px;
    overflow: hidden;
    background: var(--surface-soft);
  }

  .hero-slide {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0;
    transition: opacity 420ms var(--ease);
  }

  .hero-slide.active {
    pointer-events: auto;
    opacity: 1;
  }

  .hero-slide::after {
    position: absolute;
    inset: 0;
    content: '';
    background: linear-gradient(
      90deg,
      rgb(28 31 33 / 64%),
      rgb(28 31 33 / 26%) 48%,
      rgb(28 31 33 / 4%) 78%
    );
  }

  .hero-media {
    position: absolute;
    inset: 0;
  }

  .hero-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    min-height: min(640px, calc(100dvh - 126px));
    max-height: 680px;
    padding-block: 56px 82px;
  }

  .hero-copy {
    color: var(--white);
  }

  .hero-copy-enter-active,
  .hero-copy-leave-active {
    transition: opacity 360ms var(--ease);
  }

  .hero-copy-enter-from {
    opacity: 0;
  }

  .hero-copy-leave-to {
    opacity: 0;
  }

  .hero-eyebrow {
    display: inline-block;
    margin-bottom: 14px;
    font-size: 12px;
    font-weight: 650;
    color: rgb(255 255 255 / 84%);
    letter-spacing: 0.08em;
  }

  h1 {
    margin: 0;
    font-size: clamp(36px, 4vw, 58px);
    font-weight: 620;
    line-height: 1.14;
  }

  .hero-copy p {
    max-width: 38em;
    margin: 20px 0 28px;
    font-size: clamp(16px, 1.4vw, 18px);
    color: rgb(255 255 255 / 92%);
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .hero-button {
    display: inline-flex;
    gap: 9px;
    align-items: center;
    justify-content: center;
    min-height: 46px;
    padding: 11px 20px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    transition:
      transform var(--fast) var(--ease),
      background var(--fast) var(--ease);
  }

  .hero-button:hover {
    transform: translateY(-2px);
  }

  .hero-button-primary {
    color: var(--white);
    background: var(--accent);
    box-shadow: var(--shadow-btn);
  }

  .hero-button-primary:hover {
    background: var(--brand-strong);
  }

  .hero-button-secondary {
    color: var(--fg);
    background: rgb(255 255 255 / 94%);
    border-color: rgb(255 255 255 / 90%);
  }

  .hero-control-inner {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  .hero-controls {
    position: absolute;
    right: 0;
    bottom: 24px;
    left: 0;
    z-index: 4;
  }

  .hero-arrows {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-inline: clamp(16px, 3vw, 48px);
    pointer-events: none;
    opacity: 0;
    transition: opacity 180ms var(--ease);
  }

  .hero-dots {
    display: flex;
    gap: 8px;
  }

  .hero-dot {
    width: 34px;
    height: 4px;
    padding: 0;
    cursor: pointer;
    background: rgb(255 255 255 / 38%);
    border: 0;
  }

  .hero-dot.active {
    background: var(--white);
  }

  .hero-arrow {
    display: inline-flex;
    pointer-events: auto;
    flex: 0 0 48px;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    padding: 0;
    color: var(--white);
    cursor: pointer;
    background: rgb(28 31 33 / 18%);
    border: 1px solid rgb(255 255 255 / 72%);
    border-radius: 50%;
    transition:
      color var(--fast) var(--ease),
      background var(--fast) var(--ease),
      border-color var(--fast) var(--ease);
  }

  .hero-arrow i {
    width: 20px;
    height: 20px;
  }

  .hero-arrow:focus-visible,
  .hero-dot:focus-visible {
    outline: 2px solid var(--white);
    outline-offset: 3px;
  }

  .home-hero:focus-within .hero-arrows {
    opacity: 1;
  }

  @media (hover: hover) and (pointer: fine) {
    .home-hero:hover .hero-arrows {
      opacity: 1;
    }

    .hero-arrow:hover {
      color: var(--ink);
      background: var(--white);
      border-color: var(--white);
    }
  }

  @media (max-width: 1180px) {
    .home-hero,
    .hero-content {
      min-height: min(640px, calc(100dvh - 74px));
    }
  }

  @media (max-width: 767px) {
    .home-hero {
      min-height: 420px;
      max-height: none;
    }

    .hero-content {
      align-items: end;
      min-height: 420px;
      max-height: none;
      padding-block: 56px;
    }

    .hero-copy {
      width: 100%;
    }

    .hero-copy h1 {
      font-size: clamp(30px, 8vw, 38px);
    }

    .hero-slide::after {
      background: linear-gradient(0deg, rgb(28 31 33 / 72%), rgb(28 31 33 / 8%) 78%);
    }

    .hero-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }

    .hero-button {
      padding-inline: 14px;
    }

    .hero-arrows {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-slide,
    .hero-arrows,
    .hero-copy-enter-active,
    .hero-copy-leave-active {
      transition: none;
    }
  }
</style>
